import { useEffect, useMemo, useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { AdminFilterBar } from "../../components/admin/AdminFilterBar";
import { adminHeaders, api } from "../../lib/api";
import { shortDate } from "../../lib/format";
import { formatGlory } from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";

type GuildLevelSetting = {
  level_number: number;
  display_name: string;
  glory_required: number;
  benefits: string;
  icon_url: string;
  is_enabled: boolean;
  sort_order: number;
  note: string;
};

interface ActivitySettingRow {
  activity_code: string;
  setting_key: string;
  setting_json: string;
  is_enabled: number;
  is_frontend_visible: number;
  version: string;
  note?: string | null;
  updated_at?: string;
}

interface LevelMemberDetail {
  member: {
    member_account: string;
    status_label: string;
  };
  assets?: {
    glory_value?: number;
  };
  asset_ledgers?: Array<{
    id: number;
    asset_type: string;
    amount: number;
    balance_after?: number | null;
    activity_code: string;
    reason_code: string;
    created_at: string;
  }>;
}

const defaultLevelSettings: GuildLevelSetting[] = [
  { level_number: 1, display_name: "聚耀成員", glory_required: 0, benefits: "加入龍來喬公會，開啟基礎活動與會員身份顯示。", icon_url: "/assets/guild-levels/lv1-juyao-member.png", is_enabled: true, sort_order: 1, note: "V1 顯示設定" },
  { level_number: 2, display_name: "銅耀會員", glory_required: 1000, benefits: "累積活躍紀錄，作為後續會員權益參考。", icon_url: "/assets/guild-levels/lv2-tongyao-member.png", is_enabled: true, sort_order: 2, note: "V1 顯示設定" },
  { level_number: 3, display_name: "銀耀會員", glory_required: 3000, benefits: "提升公會身份，預留更高階會員活動資格。", icon_url: "/assets/guild-levels/lv3-yinyao-member.png", is_enabled: true, sort_order: 3, note: "V1 顯示設定" },
  { level_number: 4, display_name: "金耀會員", glory_required: 8000, benefits: "預留專屬活動、客服關懷與進階權益。", icon_url: "/assets/guild-levels/lv4-jinyao-member.png", is_enabled: true, sort_order: 4, note: "V1 顯示設定" },
  { level_number: 5, display_name: "鉑耀會員", glory_required: 15000, benefits: "預留高活躍會員加碼與身份標記。", icon_url: "/assets/guild-levels/lv5-boyao-member.png", is_enabled: true, sort_order: 5, note: "V1 顯示設定" },
  { level_number: 6, display_name: "皇耀會員", glory_required: 30000, benefits: "預留高階會員活動、節慶關懷與專屬資格。", icon_url: "/assets/guild-levels/lv6-huangyao-member.png", is_enabled: true, sort_order: 6, note: "V1 顯示設定" },
  { level_number: 7, display_name: "鑽耀貴賓", glory_required: 60000, benefits: "預留貴賓活動與人工審核優先參考。", icon_url: "/assets/guild-levels/lv7-zuanyao-vip.png", is_enabled: true, sort_order: 7, note: "V1 顯示設定" },
  { level_number: 8, display_name: "星耀貴賓", glory_required: 100000, benefits: "預留星耀級會員專屬權益與進階活動入口。", icon_url: "/assets/guild-levels/lv8-xingyao-vip.png", is_enabled: true, sort_order: 8, note: "V1 顯示設定" },
  { level_number: 9, display_name: "尊耀公爵", glory_required: 180000, benefits: "預留高階會員關懷、特別活動與身份展示。", icon_url: "/assets/guild-levels/lv9-zunyao-duke.png", is_enabled: true, sort_order: 9, note: "V1 顯示設定" },
  { level_number: 10, display_name: "龍耀公爵", glory_required: 300000, benefits: "預留最高公會身份與頂級權益設定。", icon_url: "/assets/guild-levels/lv10-longyao-duke.png", is_enabled: true, sort_order: 10, note: "V1 顯示設定" }
];

export function AdminLevelPage() {
  const isLocalDev = isLocalDevFrontend();
  const [levels, setLevels] = useState<GuildLevelSetting[]>(defaultLevelSettings);
  const [loadedFromDb, setLoadedFromDb] = useState(false);
  const [settingsUpdatedAt, setSettingsUpdatedAt] = useState("");
  const [editingLevel, setEditingLevel] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(isLocalDev ? "test005" : "");
  const [detail, setDetail] = useState<LevelMemberDetail | null>(null);
  const [memberError, setMemberError] = useState("");

  useEffect(() => {
    loadSettings();
  }, []);

  async function loadSettings() {
    setError("");
    setMessage("");
    try {
      const rows = await api<ActivitySettingRow[]>("/api/admin/activity-settings?activityCode=guild_levels", { headers: adminHeaders() });
      const row = rows.find((item) => item.setting_key === "guild_level_settings" && item.version === "v1");
      const parsed = normalizeLevelSettings(row?.setting_json);
      setLevels(parsed.length ? parsed : defaultLevelSettings);
      setLoadedFromDb(Boolean(parsed.length));
      setSettingsUpdatedAt(row?.updated_at ?? "");
    } catch (err) {
      setLevels(defaultLevelSettings);
      setLoadedFromDb(false);
      setError(err instanceof Error ? err.message : "讀取公會等級設定失敗，已載入預設資料");
    }
  }

  async function saveSettings() {
    const validation = validateLevels(levels);
    if (validation) {
      setError(validation);
      setMessage("");
      return;
    }
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await api("/api/admin/activity-settings", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          activityCode: "guild_levels",
          settingKey: "guild_level_settings",
          settingJson: normalizedForSave(levels),
          isEnabled: true,
          isFrontendVisible: true,
          version: "v1",
          operatorId: "admin",
          note: "公會等級 / 閃耀值 V1 設定"
        })
      });
      setLoadedFromDb(true);
      setMessage("公會等級設定已儲存。前台 /level 會優先讀取這份設定。");
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : "儲存公會等級設定失敗");
    } finally {
      setSaving(false);
    }
  }

  async function searchMember() {
    const trimmed = account.trim();
    if (!trimmed) {
      setMemberError("請輸入會員帳號");
      setDetail(null);
      return;
    }
    setMemberError("");
    try {
      setDetail(await api<LevelMemberDetail>(`/api/admin/members/${encodeURIComponent(trimmed)}`, { headers: adminHeaders() }));
    } catch (err) {
      setDetail(null);
      setMemberError(err instanceof Error ? err.message : "會員等級查詢失敗");
    }
  }

  const gloryValue = detail?.assets?.glory_value ?? 0;
  const levelInfo = useMemo(() => getLevelInfo(gloryValue, levels), [gloryValue, levels]);

  return (
    <AdminLayout title="公會等級 / 閃耀值">
      <section className="admin-card">
        <div className="admin-card-head">
          <span>GUILD LEVEL SETTINGS</span>
          <h2>公會等級設定總覽</h2>
        </div>
        <p className="admin-muted">目前 V1 僅提供等級設定顯示與儲存，不啟用自動升級、不提供人工調整閃耀值、不建立 reward。</p>
        <div className="admin-status-grid">
          <div><span>資料來源</span><strong>{loadedFromDb ? "activity_settings" : "前端預設資料"}</strong></div>
          <div><span>設定代號</span><strong>guild_levels / guild_level_settings</strong></div>
          <div><span>版本</span><strong>v1</strong></div>
          <div><span>最後更新</span><strong>{settingsUpdatedAt ? shortDate(settingsUpdatedAt) : "尚未儲存"}</strong></div>
        </div>
        {message ? <p className="result">{message}</p> : null}
        {error ? <p className="error-box">{error}</p> : null}
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>EDIT LEVELS</span>
          <h2>Lv1～Lv10 設定表</h2>
        </div>
        <div className="admin-actions-row">
          <button onClick={saveSettings} disabled={saving}>{saving ? "儲存中..." : "儲存公會等級設定"}</button>
          <button className="secondary" onClick={loadSettings}>重新載入</button>
        </div>
        <div className="admin-level-list">
          {levels.map((level) => (
            <article className="admin-level-editor" key={level.level_number}>
              <div className="admin-level-summary">
                <div className="admin-level-icon-preview">
                  <img src={level.icon_url} alt={`Lv${level.level_number} ${level.display_name}`} />
                </div>
                <div>
                  <span>Lv{level.level_number}</span>
                  <h3>{level.display_name}</h3>
                  <p>所需閃耀值：{formatGlory(level.glory_required)}｜排序：{level.sort_order}｜{level.is_enabled ? "啟用" : "停用"}</p>
                </div>
                <button className="secondary" onClick={() => setEditingLevel(editingLevel === level.level_number ? null : level.level_number)}>
                  {editingLevel === level.level_number ? "收合" : "編輯"}
                </button>
              </div>
              {editingLevel === level.level_number ? (
                <div className="admin-level-form">
                  <label>
                    等級名稱
                    <input value={level.display_name} onChange={(event) => updateLevel(level.level_number, { display_name: event.target.value })} />
                  </label>
                  <label>
                    所需閃耀值
                    <input type="number" min="0" value={level.glory_required} onChange={(event) => updateLevel(level.level_number, { glory_required: Number(event.target.value) })} />
                  </label>
                  <label>
                    圖示路徑
                    <input value={level.icon_url} onChange={(event) => updateLevel(level.level_number, { icon_url: event.target.value })} />
                  </label>
                  <label>
                    排序
                    <input type="number" min="1" value={level.sort_order} onChange={(event) => updateLevel(level.level_number, { sort_order: Number(event.target.value) })} />
                  </label>
                  <label className="admin-checkbox-row">
                    <input type="checkbox" checked={level.is_enabled} onChange={(event) => updateLevel(level.level_number, { is_enabled: event.target.checked })} />
                    啟用此等級
                  </label>
                  <label className="admin-form-wide">
                    權益說明
                    <textarea rows={3} value={level.benefits} onChange={(event) => updateLevel(level.level_number, { benefits: event.target.value })} />
                  </label>
                  <label className="admin-form-wide">
                    備註
                    <textarea rows={2} value={level.note} onChange={(event) => updateLevel(level.level_number, { note: event.target.value })} />
                  </label>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>MEMBER LEVEL</span>
          <h2>會員等級查詢</h2>
        </div>
        <AdminFilterBar>
          <label>
            會員帳號
            <input value={account} onChange={(event) => setAccount(event.target.value)} placeholder={isLocalDev ? "test005" : "請輸入會員帳號"} />
          </label>
          <button onClick={searchMember}>查詢</button>
          <button className="secondary" onClick={() => { setAccount(""); setDetail(null); setMemberError(""); }}>重設</button>
        </AdminFilterBar>
        {memberError ? <p className="error-box">{memberError}</p> : null}
        {detail ? (
          <div className="summary-list">
            <p>會員帳號：{detail.member.member_account}</p>
            <p>會員狀態：{detail.member.status_label}</p>
            <p>目前等級：Lv{levelInfo.current.level_number} {levelInfo.current.display_name}</p>
            <p>目前閃耀值：{formatGlory(gloryValue)}</p>
            <p>下一級：{levelInfo.next ? `Lv${levelInfo.next.level_number} ${levelInfo.next.display_name}` : "已達最高等級"}</p>
            <p>距離下一級：{levelInfo.next ? `${formatGlory(levelInfo.remainingToNext)} 閃耀值` : "0"}</p>
          </div>
        ) : null}
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>DATA STATUS</span>
          <h2>目前資料狀態</h2>
        </div>
        <div className="summary-list">
          <p>members.level_code：已存在</p>
          <p>members.glory_value：已存在</p>
          <p>glory_value_ledgers：已存在</p>
          <p>等級設定表：未新增獨立資料表，本階段沿用 activity_settings</p>
          <p>自動升級：尚未啟用</p>
          <p>人工調整：尚未啟用</p>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>GLORY LEDGER</span>
          <h2>閃耀值流水</h2>
        </div>
        {detail ? (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>時間</th>
                  <th>類型</th>
                  <th>異動</th>
                  <th>餘額</th>
                  <th>活動</th>
                  <th>原因</th>
                </tr>
              </thead>
              <tbody>
                {gloryLedgerRows(detail).map((row) => (
                  <tr key={row.id}>
                    <td>{shortDate(row.created_at)}</td>
                    <td>{row.asset_type}</td>
                    <td>{row.amount}</td>
                    <td>{row.balance_after ?? "-"}</td>
                    <td>{row.activity_code}</td>
                    <td>{row.reason_code}</td>
                  </tr>
                ))}
                {!gloryLedgerRows(detail).length ? (
                  <tr><td colSpan={6}>目前尚無該會員閃耀值流水，待後續串接完整資料。</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="admin-muted">請先查詢會員，再查看閃耀值流水。</p>
        )}
      </section>
    </AdminLayout>
  );

  function updateLevel(levelNumber: number, patch: Partial<GuildLevelSetting>) {
    setLevels((current) => current.map((level) => level.level_number === levelNumber ? { ...level, ...patch } : level));
  }
}

function gloryLedgerRows(detail: LevelMemberDetail) {
  return (detail.asset_ledgers ?? []).filter((row) => row.asset_type === "glory" || row.asset_type === "glory_value");
}

function normalizeLevelSettings(raw?: string | null) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return normalizeArray(parsed);
  } catch {
    return [];
  }
}

function normalizeArray(rows: unknown[]) {
  const normalized = rows.map((item, index) => {
    const raw = item && typeof item === "object" ? item as Record<string, unknown> : {};
    const levelNumber = Number(raw.level_number);
    const fallback = defaultLevelSettings.find((level) => level.level_number === levelNumber) ?? defaultLevelSettings[index];
    if (!fallback || !Number.isInteger(levelNumber) || levelNumber < 1 || levelNumber > 10) return null;
    const iconUrl = String(raw.icon_url ?? fallback.icon_url);
    return {
      level_number: levelNumber,
      display_name: String(raw.display_name ?? fallback.display_name),
      glory_required: Math.max(0, Number(raw.glory_required ?? fallback.glory_required)),
      benefits: String(raw.benefits ?? fallback.benefits),
      icon_url: iconUrl.startsWith("/assets/guild-levels/") ? iconUrl : fallback.icon_url,
      is_enabled: raw.is_enabled === false ? false : true,
      sort_order: Number(raw.sort_order ?? fallback.sort_order),
      note: String(raw.note ?? fallback.note)
    };
  }).filter(Boolean) as GuildLevelSetting[];
  return normalized.length === 10 ? normalized.sort((a, b) => a.sort_order - b.sort_order || a.level_number - b.level_number) : [];
}

function validateLevels(levels: GuildLevelSetting[]) {
  if (levels.length !== 10) return "公會等級設定必須包含 Lv1～Lv10";
  const seen = new Set<number>();
  for (const level of levels) {
    if (!Number.isInteger(level.level_number) || level.level_number < 1 || level.level_number > 10) return "level_number 只能是 1～10";
    if (seen.has(level.level_number)) return `Lv${level.level_number} 重複`;
    seen.add(level.level_number);
    if (!level.display_name.trim()) return `Lv${level.level_number} 等級名稱不可空白`;
    if (!Number.isFinite(Number(level.glory_required)) || Number(level.glory_required) < 0) return `Lv${level.level_number} 所需閃耀值必須是 0 以上數字`;
    if (!level.benefits.trim()) return `Lv${level.level_number} 權益說明不可空白`;
    if (!level.icon_url.startsWith("/assets/guild-levels/")) return `Lv${level.level_number} 圖示路徑必須以 /assets/guild-levels/ 開頭`;
  }
  return "";
}

function normalizedForSave(levels: GuildLevelSetting[]) {
  return levels
    .slice()
    .sort((a, b) => a.level_number - b.level_number)
    .map((level) => ({
      level_number: level.level_number,
      display_name: level.display_name.trim(),
      glory_required: Math.max(0, Number(level.glory_required)),
      benefits: level.benefits.trim(),
      icon_url: level.icon_url.trim(),
      is_enabled: Boolean(level.is_enabled),
      sort_order: Number(level.sort_order),
      note: level.note.trim()
    }));
}

function getLevelInfo(gloryValue: number, source: GuildLevelSetting[]) {
  const value = Math.max(0, Number(gloryValue ?? 0));
  const enabled = source.filter((level) => level.is_enabled).sort((a, b) => a.glory_required - b.glory_required);
  const levels = enabled.length ? enabled : defaultLevelSettings;
  const current = levels.reduce((best, level) => value >= level.glory_required ? level : best, levels[0]);
  const next = levels.find((level) => level.glory_required > value) ?? null;
  return {
    current,
    next,
    remainingToNext: next ? Math.max(0, next.glory_required - value) : 0
  };
}
